import { useEffect } from 'react';

/**
 * Global keyboard navigation for admin/handler modals.
 *
 * Mount ONCE at the App root. It attaches a single document-level keydown
 * listener that reacts only when an <input>, <textarea> or <select> is focused
 * AND that focus is inside a modal-like container.
 *
 * A modal-like container is any ancestor matching:
 *   - [role="dialog"]
 *   - [data-kb-nav]                → explicit opt-in
 *   - .fixed.inset-0               → WhatsApp-style fullscreen overlays
 *   - .modal, .Modal               → CSS convention
 *
 * Behaviour inside such a container:
 *   ArrowDown → focus next enabled input/textarea/select in the container
 *   ArrowUp   → focus previous enabled input/textarea/select
 *   Enter (on <input>, not Shift+Enter) → click the primary submit button
 *
 * Opt-out per element or subtree: add `data-kb-nav-skip`.
 */

const SUBMIT_TESTID_RE = /(submit|send|confirm|save|primary|approve|apply|ok|update|set)-?btn?$|-btn$/i;
const SUBMIT_TEXT_RE = /^(send|save|submit|confirm|update|approve|ok|set|apply|sign in|sign up|login|continue|next)$/i;

function findSubmitButton(container) {
  // 1. Explicit opt-in via data attribute
  let btn = container.querySelector('[data-kb-submit]:not([disabled])');
  if (btn) return btn;
  // 2. Native submit button
  btn = container.querySelector('button[type="submit"]:not([disabled])');
  if (btn) return btn;
  // 3. Match by data-testid suffix
  const buttons = Array.from(container.querySelectorAll('button:not([disabled])'));
  btn = buttons.find((b) => SUBMIT_TESTID_RE.test(b.getAttribute('data-testid') || ''));
  if (btn) return btn;
  // 4. Match by exact visible text (Send / Save / Submit / Confirm ...)
  btn = buttons.find((b) => SUBMIT_TEXT_RE.test((b.textContent || '').trim()));
  if (btn) return btn;
  return null;
}

export function useGlobalModalKbNav() {
  useEffect(() => {
    const onKeyDown = (e) => {
      // Don't fight local handlers that already handled it
      if (e.defaultPrevented) return;

      const t = e.target;
      const tag = (t?.tagName || '').toUpperCase();
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;

      // Skip explicit opt-out
      if (t.closest('[data-kb-nav-skip]')) return;

      // Scope to modal-like containers only
      const modal = t.closest(
        '[role="dialog"], [data-kb-nav], .fixed.inset-0, .modal, .Modal'
      );
      if (!modal) return;

      const focusables = Array.from(
        modal.querySelectorAll('input, textarea, select')
      ).filter(
        (el) =>
          !el.disabled &&
          el.type !== 'hidden' &&
          el.offsetParent !== null &&
          !el.closest('[data-kb-nav-skip]')
      );
      if (focusables.length === 0) return;
      const idx = focusables.indexOf(t);

      if (e.key === 'ArrowDown') {
        if (focusables.length < 2) return;
        e.preventDefault();
        const next = focusables[(idx + 1 + focusables.length) % focusables.length];
        next?.focus();
        try { next?.select?.(); } catch (_) {}
      } else if (e.key === 'ArrowUp') {
        if (focusables.length < 2) return;
        e.preventDefault();
        const prev = focusables[(idx - 1 + focusables.length) % focusables.length];
        prev?.focus();
        try { prev?.select?.(); } catch (_) {}
      } else if (e.key === 'Enter' && !e.shiftKey && tag === 'INPUT') {
        const btn = findSubmitButton(modal);
        if (btn) {
          e.preventDefault();
          btn.click();
        }
      }
    };
    // Bubble phase so local handlers (which use e.preventDefault) win first
    document.addEventListener('keydown', onKeyDown, false);
    return () => document.removeEventListener('keydown', onKeyDown, false);
  }, []);
}

export default useGlobalModalKbNav;
